import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../axios/axiosConfig';
import { removeJobDetail } from './jobDetailsSlice';
import { portalApi } from '../api/portalApi';

export const fetchChangeOrderRecipients = createAsyncThunk(
  'changeOrder/fetchChangeOrderRecipients',
  async ({ instance, accounts }, { rejectWithValue }) => {
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ["https://graph.microsoft.com/.default"],
        account: accounts[0],
      });

      const graphResponse = await axiosInstance.get(
        `https://graph.microsoft.com/v1.0/groups/${process.env.REACT_APP_GROUP_CHANGEORDER}/members`,
        {
          headers: {
            Authorization: `Bearer ${response.accessToken}`,
          }
        }
      );

      return graphResponse.data.value.map((member) => ({
        displayName: member.displayName,
        email: member.mail,
      }));
    } catch (error) {
      // Enhanced error handling with retry capability
      if (error.response) {
        return rejectWithValue({
          status: error.response.status,
          message: error.response.data?.error?.message || error.message
        });
      }
      return rejectWithValue({ message: error.message });
    }
  }
);

/**
 * Backward-compatible wrapper for deleteChangeOrderItem.
 * Now delegates to the RTK Query mutation while still dispatching
 * the removeJobDetail side-effect.
 */
export const deleteChangeOrderItem = createAsyncThunk(
  'changeOrder/deleteChangeOrderItem',
  async (detailId, { rejectWithValue, dispatch }) => {
    try {
      const result = await dispatch(
        portalApi.endpoints.deleteChangeOrderItem.initiate(detailId)
      );

      if (result.error) {
        const err = result.error;
        return rejectWithValue({
          status: err.status,
          message: err.data?.error?.message || err.data || 'Delete failed',
        });
      }

      dispatch(removeJobDetail({ section: 'changeOrderDetails', id: detailId }));
      return detailId;
    } catch (error) {
      if (error.response) {
        return rejectWithValue({
          status: error.response.status,
          message: error.response.data?.error?.message || error.message
        });
      }
      return rejectWithValue({ message: error.message });
    }
  }
);

const changeOrderSlice = createSlice({
  name: 'changeOrder',
  initialState: {
    changeOrderMailList: [],
    status: 'idle',
    error: null,
    deleteStatus: 'idle',
    deleteError: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchChangeOrderRecipients.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchChangeOrderRecipients.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.changeOrderMailList = action.payload;
      })
      .addCase(fetchChangeOrderRecipients.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Add cases for the delete operation
      .addCase(deleteChangeOrderItem.pending, (state) => {
        state.deleteStatus = 'loading';
      })
      .addCase(deleteChangeOrderItem.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        // Item is removed from jobDetailsSlice.changeOrderDetails in the thunk
      })
      .addCase(deleteChangeOrderItem.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload;
      });
  },
});

export const selectChangeOrderMailList = (state) => state.changeOrder.changeOrderMailList;

export default changeOrderSlice.reducer;
